import { IconButton, Spinner } from "@chakra-ui/react"
import { BlockLayoutType, ServerBlockTypeType } from "@pathwright/blocks-core"
import {
  EDITOR_LAYERING,
  useEditorStateSelector,
  useEditorUI
} from "@pathwright/blocks-engine"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import BlockSelector from "./BlockSelector"
import PasteBlockButton from "./PasteBlockButton"

// Type definitions for props
type OnPaste = (index: number, copies?: number) => void | Promise<void>
type OnSelect = (
  type: string,
  data: Record<string, any>,
  layout: string
) => void

// TypeScript wrappers for JS components
type PasteBlockButtonProps = {
  index: number
  onPaste: OnPaste
  onCancel: () => void
  copiedBlock?: unknown
  style?: React.CSSProperties
  pasting?: boolean
  error?: string
}
const TypedPasteBlockButton =
  PasteBlockButton as unknown as React.FC<PasteBlockButtonProps>

type BlockSelectorProps = {
  onSelect: OnSelect
  onClose?: () => void
}
const TypedBlockSelector =
  BlockSelector as unknown as React.FC<BlockSelectorProps>

const iconSize = 30

const AddBlocksContainer = styled.div<{
  $top: boolean
  $visible: boolean
}>`
  position: relative;
  left: 0px;
  width: 100%;
  display: flex;
  justify-content: center;
  background-color: #ccc;
  min-height: 1px;
  top: ${(p) => (p.$top ? `${0}px` : "auto")};
  bottom: ${(p) => (p.$top ? "auto" : `-${1}px`)};
  opacity: ${(p) => (p.$visible ? 1 : 0)};

  &:hover {
    opacity: 1;
  }

  &:focus-visible {
    outline: none;
  }

  &.AddBlocks--open .AddBlocks__Icon {
    z-index: ${EDITOR_LAYERING.OPEN_BLOCK_SELECTOR_ADD_BLOCK};
  }
`

const AddBlocksWrap = styled.div<{
  $forceOpen: boolean
  $copiedBlock: boolean
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: ${(p) => (p.$forceOpen ? "column" : "row")};

  &:hover {
    opacity: 1;
  }

  width: 100%;
  transition: all 0.3s;
  height: 0px;
  left: 0px;

  &.AddBlocks--open {
    z-index: ${EDITOR_LAYERING.OPEN_BLOCK_SELECTOR};
    opacity: 1;
    height: ${(p) =>
      p.$copiedBlock ? (p.$forceOpen ? "242px" : "272px") : "236px"};
    overflow: ${(p) => (p.$forceOpen && p.$copiedBlock ? "visible" : "hidden")};
  }

  ${(p) =>
    p.$forceOpen
      ? `
    & .PasteButton {
      margin-bottom: -19px;
      z-index: 1;

      .PasteButton__inner-wrapper {
        border: 1px solid rgba(150,150,150,0.2);
      }
    }
  `
      : ``}
`

const AddIconWrap = styled.div<{ $open: boolean }>`
  position: absolute;
  top: ${-iconSize / 2 - 1}px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0px;

  height: ${iconSize}px;
  width: ${iconSize}px;
  border-radius: 100%;
  background-color: #efefef;
  pointer-events: all;
  transform: rotate(${(p) => (p.$open ? 45 : 0)}deg);
  transition: all 0.3s;
`

type AddBlocksProps = {
  blockIndex: number
  onAddBlock: (
    type: string,
    data: Record<string, any>,
    layout: BlockLayoutType["key"]
  ) => void
  forceOpen: boolean
  top: boolean
  onCancelPaste: () => void
  isLastBlock: boolean
  style?: React.CSSProperties
}

const AddBlocks = ({
  blockIndex,
  onAddBlock,
  forceOpen,
  style,
  top,
  onCancelPaste,
  isLastBlock
}: AddBlocksProps) => {
  const [open, setOpen] = useState(!!forceOpen)
  const addingBlockIndex = useEditorStateSelector(
    (state) => state.addingBlockIndex
  )
  const isAdding = addingBlockIndex === blockIndex
  const addBlockRef = useRef<HTMLDivElement | null>(null)
  const copiedBlock = useEditorStateSelector((state) => state.copiedBlockClip)
  const pastingBlockIndex = useEditorStateSelector(
    (state) => state.pastingBlockIndex
  )
  const pasteError = useEditorStateSelector((state) => state.pasteError)
  const blocksCount = useEditorStateSelector(
    (state) => state.blocks?.length ?? 0
  )
  const onPasteBlock = useEditorStateSelector((state) => state.handlePasteBlock)
  const { triggeredAddBlock, setTriggeredAddBlock } = useEditorUI()

  // Close the block selector after copying a block to the clipboard
  useEffect(() => {
    // Don't close the block selector if there are no blocks
    // (otherwise, we can't add blocks)
    if (!blocksCount) return
    if (copiedBlock) {
      setOpen(false)
    }
  }, [copiedBlock, blocksCount])

  // Allows opening the block selector externally (ex: keyboard shortcut)
  useEffect(() => {
    const triggerAsLastBlock = triggeredAddBlock === -1 && isLastBlock
    const triggerAsBlockIndex = triggeredAddBlock === blockIndex
    if ((triggerAsLastBlock || triggerAsBlockIndex) && !top) {
      toggleOpen()
      setTriggeredAddBlock?.(null)
    }
  }, [triggeredAddBlock])

  useEffect(() => {
    if (open) {
      addBlockRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      })
      addBlockRef.current?.focus()
    }
  }, [open])

  const toggleOpen = () => setOpen(!open)

  const handleAddBlock = (
    blockType: ServerBlockTypeType["type"],
    data: Record<string, any>,
    layout: BlockLayoutType["key"]
  ) => {
    onAddBlock(blockType, data, layout)
    setOpen(false)
  }

  return (
    <AddBlocksContainer
      className={`AddBlocks${!copiedBlock && open ? ` AddBlocks--open` : ""}`}
      style={style}
      $top={top}
      $visible={open}
      ref={addBlockRef}
      tabIndex={0}
    >
      <AddBlocksWrap
        className={
          (!copiedBlock && open) || forceOpen
            ? "AddBlocks--open"
            : "AddBlocks--closed"
        }
        $copiedBlock={!!copiedBlock || isAdding}
        $forceOpen={forceOpen}
        tabIndex={0}
      >
        {forceOpen ? (
          <TypedPasteBlockButton
            index={blockIndex}
            copiedBlock={copiedBlock}
            onPaste={onPasteBlock}
            onCancel={onCancelPaste}
            pasting={pastingBlockIndex === blockIndex}
            error={pasteError}
          />
        ) : null}
        {(!copiedBlock && open) || forceOpen ? (
          isAdding ? (
            <Spinner
              size="lg"
              color="gray.500"
              bg="transparent"
              _hover={{
                bg: "transparent"
              }}
              h={30}
              w={30}
              fontSize="2rem"
            />
          ) : (
            <TypedBlockSelector
              onSelect={handleAddBlock}
              onClose={toggleOpen}
            />
          )
        ) : null}
      </AddBlocksWrap>
      {!forceOpen && (
        <AddIconWrap
          className="AddBlocks__Icon"
          onClick={toggleOpen}
          $open={!copiedBlock && open}
        >
          {copiedBlock ? (
            <TypedPasteBlockButton
              index={blockIndex}
              copiedBlock={copiedBlock}
              onPaste={onPasteBlock}
              onCancel={onCancelPaste}
              pasting={pastingBlockIndex === blockIndex}
              error={pasteError}
            />
          ) : (
            <IconButton
              aria-label={isAdding ? "Adding block" : "Add block"}
              loading={isAdding}
              color="#888687"
              size="xs"
              rounded="full"
              variant="plain"
            >
              <Pathicon icon="plus" />
            </IconButton>
          )}
        </AddIconWrap>
      )}
    </AddBlocksContainer>
  )
}

export default AddBlocks
